import { CheckCircleIcon } from "lucide-react";
import { createContext, useCallback, useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { Form } from "../form";
import { cn } from "../../lib";

export interface MultiStepFormV2Config<T extends FieldValues> {
  id: string;
  label: (form: T) => string;
  description?: (form: T) => React.ReactNode;
  subText?: (form: T) => string | React.ReactNode;
  additionalInfo?: (form: T) => React.ReactNode;
  isComplete?: (form: T) => boolean;
  isNavigable?: (form: T) => boolean;
  show?: (form: T) => boolean;
  component?: React.FC<{
    next: () => void;
    prev: () => void;
    goTo: (id: string) => void;
  }>;
  subSteps?: MultiStepFormV2Config<T>[];
}

export type MultiStepFormV2ContextValue = {
  currentStepId: string;
  nextStep: () => void;
  prevStep: () => void;
  goTo: (id: string) => void;
};

const MultiStepFormV2Context =
  createContext<MultiStepFormV2ContextValue | null>(null);

function flattenForNavigation<T extends FieldValues>(
  configs: MultiStepFormV2Config<T>[]
): MultiStepFormV2Config<T>[] {
  return configs.flatMap((c) => (c.subSteps?.length ? c.subSteps : [c]));
}

function useVisibleConfigs<T extends FieldValues>(
  config: MultiStepFormV2Config<T>[],
  form: UseFormReturn<T>
) {
  const values = form.watch();
  return useMemo(
    () => config.filter((c) => !c.show || c.show(values)),
    [config, values]
  );
}

function useFlatNavConfigs<T extends FieldValues>(
  config: MultiStepFormV2Config<T>[],
  form: UseFormReturn<T>
) {
  const values = form.watch();
  return useMemo(() => {
    const flat = flattenForNavigation(config);
    return flat.filter((c) => !c.show || c.show(values));
  }, [config, values]);
}

export function MultiStepFormV2<T extends FieldValues>(props: {
  config: MultiStepFormV2Config<T>[];
  defaultStep: number;
  title: string | React.ReactNode;
  form: UseFormReturn<T>;
  onComplete?: () => void;
}) {
  const { config, defaultStep, title, form, onComplete } = props;

  const flatNavConfigs = useFlatNavConfigs(config, form);

  const [currentStepId, setCurrentStepId] = useState(
    () => flatNavConfigs[defaultStep]?.id ?? flatNavConfigs[0]?.id
  );

  const currentConfig = useMemo(() => {
    // eslint-disable-next-line
    for (const c of config) {
      if (c.id === currentStepId) return c;
      const sub = c.subSteps?.find((s) => s.id === currentStepId);
      if (sub) return sub;
    }
    return undefined;
  }, [config, currentStepId]);

  const nextStep = useCallback(() => {
    const idx = flatNavConfigs.findIndex((c) => c.id === currentStepId);
    if (idx >= flatNavConfigs.length - 1) {
      onComplete?.();
      return;
    }
    setCurrentStepId(flatNavConfigs[idx + 1].id);
  }, [flatNavConfigs, currentStepId, onComplete]);

  const prevStep = useCallback(() => {
    const idx = flatNavConfigs.findIndex((c) => c.id === currentStepId);
    if (idx > 0) {
      setCurrentStepId(flatNavConfigs[idx - 1].id);
    }
  }, [flatNavConfigs, currentStepId]);

  const goToStep = useCallback(
    (id: string) => {
      const allIds = new Set(flattenForNavigation(config).map((c) => c.id));
      if (!allIds.has(id)) {
        throw new Error(`Step with id ${id} not found`);
      }
      setCurrentStepId(id);
    },
    [config]
  );

  const context = useMemo(
    () => ({
      currentStepId,
      nextStep,
      prevStep,
      goTo: goToStep,
    }),
    [currentStepId, nextStep, prevStep, goToStep]
  );

  const Component = currentConfig?.component;

  return (
    <MultiStepFormV2Context.Provider value={context}>
      <div className="flex flex-row size-full min-h-0 bg-card">
        <div className="w-75 xl:w-90 max-w-1/2 shrink-0 flex flex-col gap-4 py-6 px-4">
          <span className="font-semibold text-lg">{title}</span>
          <MultiStepFormV2Steps
            config={config}
            form={form}
            currentStepId={currentStepId}
            goTo={goToStep}
          />
        </div>
        <div className="h-full w-full">
          <Form {...form}>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background h-full border border-border">
              {Component && (
                <Component next={nextStep} prev={prevStep} goTo={goToStep} />
              )}
            </div>
          </Form>
        </div>
      </div>
    </MultiStepFormV2Context.Provider>
  );
}

export function MultiStepFormV2Steps<T extends FieldValues>(props: {
  config: MultiStepFormV2Config<T>[];
  form: UseFormReturn<T>;
  currentStepId: string;
  goTo: (id: string) => void;
}) {
  const { config, form, currentStepId, goTo } = props;
  const visibleConfigs = useVisibleConfigs(config, form);

  const currentVisibleIndex = visibleConfigs.findIndex(
    (c) =>
      c.id === currentStepId || c.subSteps?.some((s) => s.id === currentStepId)
  );

  return (
    <div className="flex flex-col gap-4">
      {visibleConfigs.map((stepConfig, idx) => (
        <Step<T>
          key={stepConfig.id}
          form={form}
          config={stepConfig}
          visibleIndex={idx}
          currentVisibleIndex={currentVisibleIndex}
          currentStepId={currentStepId}
          goTo={goTo}
        />
      ))}
    </div>
  );
}

function Step<T extends FieldValues>(props: {
  config: MultiStepFormV2Config<T>;
  visibleIndex: number;
  currentVisibleIndex: number;
  currentStepId: string;
  form: UseFormReturn<T>;
  goTo: (id: string) => void;
}) {
  const { form, goTo } = props;
  const values = form.watch();
  const isCompleted = props.config.isComplete?.(values);
  const isNavigable = props.config.isNavigable?.(values);
  const isCurrent = props.visibleIndex === props.currentVisibleIndex;

  const visibleSubSteps = useMemo(
    () => props.config.subSteps?.filter((s) => !s.show || s.show(values)) ?? [],
    [props.config.subSteps, values]
  );

  const additionalInfo = useMemo(
    () => props.config.additionalInfo?.(values) || null,
    [values, props.config]
  );

  const label = useMemo(
    () => props.config.label(values),
    [values, props.config]
  );
  const description = useMemo(
    () => props.config.description?.(values),
    [values, props.config]
  );

  const stepNumber = props.visibleIndex + 1;

  return (
    <div
      role="button"
      onClick={() => {
        if (props.config.isNavigable?.(form.getValues())) {
          if (visibleSubSteps.length > 0) {
            const incompleteStep = visibleSubSteps.find(
              (s) => !s.isComplete?.(form.getValues())
            );
            goTo(incompleteStep?.id || visibleSubSteps[0]?.id);
          } else {
            goTo(props.config.id);
          }
        }
      }}
      className={cn(
        "p-4 border flex flex-col rounded-lg border-transparent transition-colors duration-150 bg-transparent",
        isCurrent && "border-foreground rounded-lg bg-background",
        !isNavigable && "pointer-events-none"
      )}
    >
      <div
        className={cn(
          "flex flex-row gap-2 items-center cursor-default",
          !isCompleted && !isCurrent && "text-muted-foreground"
        )}
      >
        {isCurrent ? (
          <div className="rounded-full text-xs font-semibold flex items-center justify-center bg-tag-blue-foreground size-4 text-background self-start mt-0.5">
            {stepNumber}
          </div>
        ) : isCompleted ? (
          <CheckCircleIcon className="size-4 stroke-constructive self-start mt-0.5" />
        ) : (
          <div className="rounded-full font-semibold text-xs flex items-center justify-center bg-transparent size-4 text-muted-foreground self-start mt-0.5">
            {stepNumber}
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          <span className={cn("text-sm", isCurrent && "font-semibold")}>
            {label}
          </span>
          {description && (
            <span className="text-xs text-muted-foreground">{description}</span>
          )}
        </div>
        <span className="ml-auto text-sm font-medium">
          {props.config.subText?.(values)}
        </span>
      </div>
      {(isCurrent || isNavigable) && visibleSubSteps.length > 0 && (
        <div className="flex flex-col pl-3 pt-4 pb-2 gap-2">
          {visibleSubSteps.map((subStep) => (
            <SubStepItem<T>
              key={subStep.id}
              config={subStep}
              form={form}
              isCurrent={subStep.id === props.currentStepId}
              goTo={goTo}
            />
          ))}
        </div>
      )}
      {isNavigable || isCurrent ? additionalInfo : null}
    </div>
  );
}

function SubStepItem<T extends FieldValues>(props: {
  config: MultiStepFormV2Config<T>;
  form: UseFormReturn<T>;
  isCurrent: boolean;
  goTo: (id: string) => void;
}) {
  const { form, goTo, isCurrent } = props;
  const values = form.watch();

  const isNavigable = props.config.isNavigable?.(values);

  const label = useMemo(
    () => props.config.label(values),
    [values, props.config]
  );
  const additionalInfo = useMemo(
    () => props.config.additionalInfo?.(values) || null,
    [values, props.config]
  );

  return (
    <div
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        if (isNavigable || isCurrent) {
          goTo(props.config.id);
        }
      }}
      className={cn(
        "p-3 py-2 rounded-md flex flex-col transition-colors duration-150",
        isCurrent ? "bg-secondary/50" : "hover:bg-secondary/30",
        !isNavigable && !isCurrent && "pointer-events-none opacity-60"
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn("text-sm")}>{label}</span>
        <span className="ml-auto text-sm font-medium text-muted-foreground">
          {props.config.subText?.(values)}
        </span>
      </div>
      {(isNavigable || isCurrent) && additionalInfo}
    </div>
  );
}
